// ListFilterSaveDialog - MODULE
// ***********************************************************************************************************************
import { Box, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ApplicationPermissionType, ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { IListFilterItem } from "../../../../models/common/listFilterItem";
import { SavedListFilter } from "../../../../models/common/savedListFilter";
import * as filterService from "../../../../models/services/filterService";
import { ISessionState } from "../../../../models/session/sessionState";
import { Dialog } from "../../Dialog/Dialog";
import { FormField } from "../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";

//ListFilterSaveDialog
export interface IListFilterSaveDialogProp {
    sessionState: ISessionState;
    classes?: string;
    companyId: string;
    applicationPermissionType: ApplicationPermissionType;
    listFilter: IListFilterItem;
    generalFilter: any;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    reservedNames: string[];
    onGetGeneralFilter?: () => any;
    onOk: (savedListFilter: SavedListFilter, isShared: boolean) => void;
    onCancel: () => void;
}

export interface IListFilterSaveDialogState {
    name: string;
    shared: boolean
}

export class ListFilterSaveDialog extends React.Component<IListFilterSaveDialogProp, IListFilterSaveDialogState> {
    private orgStateHash: string = "";

    constructor(props: IListFilterSaveDialogProp) {
        super(props);
        this.state = {
            name: props.listFilter.getTitle(),
            shared: props.sessionState.isCompanyAdmin ? props.listFilter.isShared : false
        };
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "name") {
            this.setState({ name: value });
        }
    };

    changeSharedFilter = (shared: boolean) => {
        this.setState({ shared });
    };

    static getSavedListFilter = (name: string, generalFilter: any, sortColumn: string, sortOrderIsAsc: boolean, applicationPermissionType: number): SavedListFilter => {
        const result = new SavedListFilter();
        result.id = name;
        result.title = name;
        result.applicationPermissionType = applicationPermissionType;
        result.generalFilter = generalFilter ?? {};
        result.sortColumn = sortColumn;
        result.sortOrderIsAsc = sortOrderIsAsc;
        return result;
    };

    getSavedListFilterFromState = (props: IListFilterSaveDialogProp, state: IListFilterSaveDialogState, beforeSave: boolean): SavedListFilter => {
        let generalFilter = props.generalFilter;
        if (beforeSave && props.onGetGeneralFilter) {
            generalFilter = props.onGetGeneralFilter();
        }
        return ListFilterSaveDialog.getSavedListFilter(state.name, generalFilter, props.sortColumn, props.sortOrderIsAsc, props.applicationPermissionType);
    };

    static getSaveData = (companyId: string, orgListFilterId: string, shared: boolean, applicationPermissionType: number, savedListFilter: SavedListFilter): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", companyId);
        result.append("applicationPermissionType", applicationPermissionType);
        result.append("orgId", !Base.isNullOrUndefined(orgListFilterId) ? orgListFilterId : "");
        result.append("isShared", shared);
        result.append("item", savedListFilter);
        return result;
    };

    getSaveDataFromState = (props: IListFilterSaveDialogProp, state: IListFilterSaveDialogState, savedListFilter: SavedListFilter): SaveDataObj => {
        return ListFilterSaveDialog.getSaveData(props.companyId, null, state.shared, props.applicationPermissionType, savedListFilter);
    };
    
    getHashFromState = (props: IListFilterSaveDialogProp, state: IListFilterSaveDialogState): string => {
        return this.getSaveDataFromState(props, state, this.getSavedListFilterFromState(props, state, false)).hash;
    };

    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.name) {
                AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                return resolve(false);
            }
            if (props.reservedNames.find(i => i.equalIgnoreCase(state.name))) {
                AppUtils.showErrorMessage(Translations.NameIsAlreadyInUse);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SavedListFilter> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const savedListFilter = this.getSavedListFilterFromState(props, state, true);
        const saveData = this.getSaveDataFromState(props, state, savedListFilter);
        //Call server
        const result = await AppUtils.callService(() => filterService.saveFilter(saveData.obj));
        if (!result) return null;
        this.orgStateHash = saveData.hash;
        return savedListFilter;
    };

    handleSave = async () => {
        const props = this.props;
        const result = await this.saveToDb();
        if (!result) return;
        props.onOk(result, this.state.shared);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave();
        } else {
            props.onCancel();
        }
    };

    static saveListFilterToDb = async (listFilter: IListFilterItem, generalFilter: any, sortColumn: string, sortOrderIsAsc: boolean, companyId: string, applicationPermissionType: number): Promise<SavedListFilter> => {
        if (!listFilter) return;
        const savedListFilter = ListFilterSaveDialog.getSavedListFilter(listFilter.getTitle(), generalFilter, sortColumn, sortOrderIsAsc, applicationPermissionType);
        const saveData = ListFilterSaveDialog.getSaveData(companyId, listFilter.id, listFilter.isShared, applicationPermissionType, savedListFilter);
        const result = await AppUtils.callService(() => filterService.saveFilter(saveData.obj));
        if (!result) return null;
        return savedListFilter;
    };

    static removeListFilterFromDb = async (savedListFilters: IListFilterItem[], companyId: string, filterId: string, isShared: boolean, applicationPermissionType: number): Promise<ApiSuccess> => {
        if (!filterId) return;
        const savedListFilter = savedListFilters.find(i => i.id === filterId);
        if (!savedListFilter) return;
        const confirmationResult = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveFilterParameter, savedListFilter.title));
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;
        const saveData = new SaveDataObj();
        saveData.append("companyId", companyId);
        saveData.append("applicationPermissionType", applicationPermissionType);
        saveData.append("id", filterId);
        saveData.append("isShared", isShared);
        return await AppUtils.callService(() => filterService.removeFilter(saveData.obj), false);
    };

    render() {
        const state = this.state;
        return (
            <Dialog
                id="list-filter-save-dialog"
                maxWidth="sm"
                title={Translations.SaveFilterAs}
                body={
                    <Box sx={{ marginTop: "-1.5rem" }}>
                        <FormField title={Translations.Name} required fullWidth>
                            <TextField type="text" name="name" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.name} required={true} onChange={this.handleChange} size="small" fullWidth autoFocus />
                        </FormField>
                    </Box>
                }
                buttons={[
                    { title: Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.Save, variant: "contained", onClick: this.handleSave },
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}